import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TOOLS, ZOOM_MAX, ZOOM_MIN } from '../constants';
import { usePdfEditorStore } from '../state/usePdfEditorStore';
import { useImagePlacements } from './useImagePlacements';
import PageView from './PageView';
import Ruler, { RULER_SIZE } from './Ruler';
import { useStrippedPreview } from './useStrippedPreview';

const GAP = 40;
const PADDING = 32;
/** Where the ruler shades a margin: the 1-inch default most documents use. */
const PAGE_MARGIN_POINTS = 72;

/**
 * Scrolling page area. Owns the zoom→scale mapping (fit-width / fit-page /
 * manual), lazily mounts pages, keeps the page indicator in sync with scroll,
 * and implements hand-tool panning.
 */
export default function Viewport({
    labels,
    searchHitsByPage,
    activeHitId,
    onScaleChange,
    // A split pane is a second window onto the same document. Only one of them
    // may own "which page am I on" — two panes both writing it would fight
    // every time either was scrolled.
    syncScroll = true,
    plain = false,
    onReplaceImage,
}) {
    const scrollRef = useRef(null);
    const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
    // Where the rulers have to draw: how long the scrollable content is, where
    // the current page starts inside it, and how far the box is scrolled — the
    // last one is what keeps each strip pinned to its edge.
    // Where the current page starts inside each strip. Nothing here changes on
    // a scroll — only on a relayout — so the strips never re-render while the
    // wheel is turning.
    const [rulerOrigin, setRulerOrigin] = useState({ x: 0, y: 0 });
    const hRulerRef = useRef(null);
    const vRulerRef = useRef(null);
    const columnRef = useRef(null);
    const panRef = useRef(null);
    const suppressScrollSync = useRef(false);
    const settleRef = useRef(null);

    const pages = usePdfEditorStore((s) => s.pages);
    const pdf = usePdfEditorStore((s) => s.pdf);
    const objectsByUid = usePdfEditorStore((s) => s.objectsByUid);
    // Where the source document's own pictures are, for the replace tool.
    const imagePlacements = useImagePlacements();
    const zoom = usePdfEditorStore((s) => s.zoom);
    const fitMode = usePdfEditorStore((s) => s.fitMode);
    const viewMode = usePdfEditorStore((s) => s.viewMode);
    const currentPage = usePdfEditorStore((s) => s.currentPage);
    const scrollNonce = usePdfEditorStore((s) => s.scrollNonce);
    const activeTool = usePdfEditorStore((s) => s.activeTool);
    const showRuler = usePdfEditorStore((s) => s.showRuler);
    // The page to draw, once "Edit text" has taken a paragraph out of it.
    const strippedPreview = useStrippedPreview();
    const setCurrentPage = usePdfEditorStore((s) => s.setCurrentPage);
    const setZoom = usePdfEditorStore((s) => s.setZoom);

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;
        const measure = () => setViewportSize({ width: el.clientWidth, height: el.clientHeight });
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Widest / tallest page drives fit modes so no page ever overflows.
    const widest = pages.reduce((max, p) => {
        const w = p.userRotation % 180 === 90 ? p.height : p.width;
        return Math.max(max, w);
    }, 1);
    const tallest = pages.reduce((max, p) => {
        const h = p.userRotation % 180 === 90 ? p.width : p.height;
        return Math.max(max, h);
    }, 1);

    let scale = zoom;
    if (fitMode === 'width' && viewportSize.width) {
        scale = Math.max(ZOOM_MIN, (viewportSize.width - PADDING * 2) / widest);
    } else if (fitMode === 'page' && viewportSize.width && viewportSize.height) {
        scale = Math.max(ZOOM_MIN, Math.min(
            (viewportSize.width - PADDING * 2) / widest,
            (viewportSize.height - PADDING * 2) / tallest,
        ));
    }
    scale = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale));

    useEffect(() => { onScaleChange?.(scale); }, [scale, onScaleChange]);

    /**
     * How far into each strip the current page begins.
     *
     * Measured from the rendered boxes rather than from the layout constants:
     * both are read in the same instant, so whatever the box is scrolled to
     * cancels out and the answer is a content-space offset either way.
     */
    const measureRulers = useCallback(() => {
        const el = scrollRef.current;
        const page = el?.querySelector(`[data-page-index="${currentPage}"]`);
        if (!page) return;
        const rect = page.getBoundingClientRect();
        const h = hRulerRef.current?.getBoundingClientRect();
        const v = vRulerRef.current?.getBoundingClientRect();
        const next = {
            x: h ? Math.round(rect.left - h.left) : 0,
            y: v ? Math.round(rect.top - v.top) : 0,
        };
        setRulerOrigin((prev) => (prev.x === next.x && prev.y === next.y ? prev : next));
    }, [currentPage]);

    useLayoutEffect(() => {
        measureRulers();
    }, [measureRulers, scale, pages.length, showRuler, viewMode, viewportSize.width, viewportSize.height]);

    /* --------------------------------------------------------- scroll sync */

    const handleScroll = useCallback(() => {
        if (!syncScroll || suppressScrollSync.current) return;
        const el = scrollRef.current;
        if (!el) return;
        const nodes = el.querySelectorAll('[data-page-index]');
        const mid = el.scrollTop + el.clientHeight * 0.35;
        let best = 0;
        for (const node of nodes) {
            if (node.offsetTop <= mid) best = Number(node.dataset.pageIndex);
            else break;
        }
        if (best !== currentPage) setCurrentPage(best);
    }, [currentPage, setCurrentPage, syncScroll]);

    /**
     * Scroll a page into view when it is selected elsewhere (thumbnails, search).
     *
     * Sync stays suppressed until the scroll has actually *stopped*. A fixed
     * timer was not enough: a smooth scroll across twenty pages outlives it, and
     * the handler then reads wherever the animation happens to be and says that
     * is the current page — so a bookmark added straight after a jump landed on
     * the wrong page.
     */
    const scrollToPage = useCallback((index) => {
        const el = scrollRef.current;
        const node = el?.querySelector(`[data-page-index="${index}"]`);
        if (!el || !node) return;

        suppressScrollSync.current = true;
        clearTimeout(settleRef.current);
        const release = () => {
            clearTimeout(settleRef.current);
            el.removeEventListener('scrollend', release);
            suppressScrollSync.current = false;
        };
        // `scrollend` where it exists; a generous timer everywhere else, and as
        // a backstop for a scroll that never moves (already on the page).
        el.addEventListener('scrollend', release, { once: true });
        settleRef.current = setTimeout(release, 1200);

        el.scrollTo({ top: Math.max(0, node.offsetTop - PADDING), behavior: 'smooth' });
    }, []);

    // `scrollNonce` only bumps when something explicitly navigates (thumbnail,
    // search hit, page box) — clicking on a page must not yank the scroll.
    useEffect(() => {
        if (syncScroll && scrollNonce) scrollToPage(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollNonce]);

    /* ------------------------------------------------------------- zooming */

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;
        const onWheel = (e) => {
            if (!e.ctrlKey && !e.metaKey) return;
            e.preventDefault();
            setZoom(scale * (e.deltaY < 0 ? 1.1 : 0.9));
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [scale, setZoom]);

    /* -------------------------------------------------------------- panning */

    const onPanStart = (e) => {
        if (activeTool !== TOOLS.HAND || e.button !== 0) return;
        const el = scrollRef.current;
        panRef.current = {
            x: e.clientX,
            y: e.clientY,
            left: el.scrollLeft,
            top: el.scrollTop,
        };
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const onPanMove = (e) => {
        const pan = panRef.current;
        if (!pan || !scrollRef.current) return;
        scrollRef.current.scrollLeft = pan.left - (e.clientX - pan.x);
        scrollRef.current.scrollTop = pan.top - (e.clientY - pan.y);
    };
    const onPanEnd = () => { panRef.current = null; };

    const twoUp = viewMode === 'two-up';
    const rulers = showRuler && !twoUp && !!pages[currentPage];
    const current = pages[currentPage];
    const hv = rulers ? {
        horizontal: {
            ref: hRulerRef,
            length: current.width,
            scale,
            margin: PAGE_MARGIN_POINTS,
            origin: rulerOrigin.x,
        },
        vertical: {
            ref: vRulerRef,
            orientation: 'vertical',
            length: current.userRotation % 180 === 90 ? current.width : current.height,
            scale,
            margin: PAGE_MARGIN_POINTS,
            origin: rulerOrigin.y,
        },
    } : null;

    return (
        <div
            ref={scrollRef}
            onScroll={handleScroll}
            onPointerDown={onPanStart}
            onPointerMove={onPanMove}
            onPointerUp={onPanEnd}
            onPointerCancel={onPanEnd}
            className={`relative h-full min-h-0 flex-1 overflow-auto bg-[#E8E4E0] [scrollbar-width:thin] dark:bg-zinc-950 ${
                activeTool === TOOLS.HAND ? 'cursor-grab active:cursor-grabbing' : ''
            }`}
        >
            {/* A row the width of the content: the side ruler is its first
                column, the pages the second. Both strips are `position: sticky`
                and nothing about them runs on scroll — the compositor holds them
                to their edge, which is the only way they keep up with a wheel.
                Sticky could not do this before only because a block child of a
                scroller takes the VISIBLE width; inside this row each strip's
                containing block is the content instead. */}
            <div className="flex w-max min-w-full items-stretch">
                {rulers ? <Ruler {...hv.vertical} /> : null}

                <div className="relative flex-1">
                    {rulers ? <Ruler {...hv.horizontal} /> : null}

                    <div
                        ref={columnRef}
                        className={`mx-auto flex w-max ${twoUp ? 'flex-row flex-wrap justify-center' : 'flex-col items-center'}`}
                        style={{
                            gap: GAP,
                            padding: PADDING,
                            // Room for the top strip, so no page sits under it.
                            paddingTop: (twoUp ? PADDING : 12) + (rulers ? RULER_SIZE : 0),
                            paddingBottom: PADDING + 24,
                        }}
                    >
                        {pages.map((page, index) => (
                            <PageView
                                key={page.uid}
                                page={page}
                                index={index}
                                pdf={pdf}
                                basePdf={strippedPreview.doc}
                                strippedIds={strippedPreview.stripped}
                                scale={scale}
                                objects={objectsByUid[page.uid] || EMPTY}
                                searchHits={searchHitsByPage?.[index]}
                                activeHitId={activeHitId}
                                onRequestFocus={setCurrentPage}
                                labels={labels}
                                plain={plain}
                                imagePlacements={imagePlacements[page.uid]}
                                onReplaceImage={onReplaceImage}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

const EMPTY = [];
